import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Markdown } from '@/components/Markdown'
import { Turnstile } from '@/components/Turnstile'
import { publicApi } from '@/lib/api'
import { usePage } from '@/lib/queries'
import { canonicalURL, useSeo } from '@/lib/seo'
import { useLocale } from '@/lib/useLocale'

type Status = 'idle' | 'sending' | 'sent' | 'error'

export function ContactsPage() {
  const locale = useLocale()
  const { t } = useTranslation()
  const { data: page } = usePage(locale, 'contacts')

  const [status, setStatus] = useState<Status>('idle')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [turnstileToken, setTurnstileToken] = useState('')
  // A Turnstile token may be used once, so every attempt asks for a new one.
  const [turnstileAttempt, setTurnstileAttempt] = useState(0)

  useSeo({
    title: page?.metaTitle || `${t('nav.contacts')} — ${t('site.name')}`,
    description: page?.metaDescription || t('site.defaultDescription'),
    path: '/contacts',
    locale,
    structuredData: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: page?.title ?? t('contacts.title'),
        url: canonicalURL(locale, '/contacts'),
        inLanguage: locale,
      },
    ],
  })

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)

    setStatus('sending')
    setFieldErrors({})

    try {
      await publicApi.contact({
        name: text(data, 'name'),
        email: text(data, 'email'),
        message: text(data, 'message'),
        locale,
        turnstileToken: turnstileToken || undefined,
        // The honeypot: a real person never sees this field, so anything in it
        // came from a bot filling the form in blind.
        website: text(data, 'website'),
      })
      setStatus('sent')
      form.reset()
    } catch (error) {
      const details = (error as { details?: Record<string, string> }).details
      if (details && Object.keys(details).length > 0) {
        setFieldErrors(details)
      }
      setStatus('error')
    } finally {
      setTurnstileAttempt((n) => n + 1)
    }
  }

  return (
    <div className="container-page py-14">
      <div className="grid gap-14 lg:grid-cols-2">
        <div>
          <h1 className="text-4xl sm:text-5xl">{page?.title ?? t('contacts.title')}</h1>
          <Markdown source={page?.body ?? ''} className="mt-8" />
        </div>

        <div>
          <h2 className="text-2xl">{t('contacts.formTitle')}</h2>

          {status === 'sent' ? (
            <div className="mt-6 rounded-[var(--radius-card)] border border-stone-200 bg-white p-6">
              <p className="text-stone-700">{t('contacts.success')}</p>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="btn btn-secondary mt-4"
              >
                {t('contacts.sendAnother')}
              </button>
            </div>
          ) : (
            <form onSubmit={(event) => void onSubmit(event)} className="mt-6 space-y-4" noValidate>
              <Field
                name="name"
                label={t('contacts.name')}
                error={fieldErrors.name}
                autoComplete="name"
                required
              />
              <Field
                name="email"
                type="email"
                label={t('contacts.email')}
                error={fieldErrors.email}
                autoComplete="email"
                required
              />

              <div>
                <label htmlFor="message" className="label">
                  {t('contacts.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  required
                  placeholder={t('contacts.messagePlaceholder')}
                  aria-invalid={fieldErrors.message ? true : undefined}
                  aria-describedby={fieldErrors.message ? 'message-error' : undefined}
                  className="field resize-y"
                />
                {fieldErrors.message && (
                  <p id="message-error" className="mt-1 text-sm text-red-600">
                    {fieldErrors.message}
                  </p>
                )}
              </div>

              {/* Hidden from people and from screen readers, visible to bots. */}
              <div aria-hidden="true" className="absolute -left-[9999px]">
                <label htmlFor="website">Website</label>
                <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
              </div>

              <Turnstile
                locale={locale}
                resetKey={turnstileAttempt}
                onToken={(token) => setTurnstileToken(token)}
              />

              {status === 'error' && Object.keys(fieldErrors).length === 0 && (
                <p role="alert" className="text-sm text-red-600">
                  {t('contacts.error')}
                </p>
              )}

              <button type="submit" disabled={status === 'sending'} className="btn btn-primary">
                {status === 'sending' ? t('contacts.sending') : t('contacts.send')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

/** FormData entries are string | File; only the strings are of interest here. */
function text(data: FormData, name: string): string {
  const value = data.get(name)
  return typeof value === 'string' ? value : ''
}

function Field({
  name,
  label,
  error,
  type = 'text',
  autoComplete,
  required,
}: {
  name: string
  label: string
  error?: string
  type?: string
  autoComplete?: string
  required?: boolean
}) {
  return (
    <div>
      <label htmlFor={name} className="label">
        {label}
      </label>
      <input
        id={name}
        name={name}
        type={type}
        required={required}
        autoComplete={autoComplete}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${name}-error` : undefined}
        className="field"
      />
      {error && (
        <p id={`${name}-error`} className="mt-1 text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
